import { type Pagination, type PaginationData, type TransactionItemDetail, createTransactionItemDetail } from 'models';
import { type InfiniteData, type QueryKey, type UseInfiniteQueryOptions, useInfiniteQuery } from 'query';
import type { ApiClientResponseError, DefaultResponseErrorDataObject } from 'common/services';
import { getTransactionItems } from '../services';
import type { TransactionsListFilter } from '../types';
import { getTransactionsInfiniteQueryKey } from './keys';

type P = {
  filter: TransactionsListFilter;
  options?: UseInfiniteQueryOptions<
    PaginationData<TransactionItemDetail>,
    ApiClientResponseError<DefaultResponseErrorDataObject>,
    InfiniteData<PaginationData<TransactionItemDetail>>,
    QueryKey,
    string | undefined
  >;
  pagination: Omit<Pagination, 'position'>;
};

export const useTransactionItemsInfiniteQuery = ({ filter, options, pagination }: P) =>
  useInfiniteQuery<
    PaginationData<TransactionItemDetail>,
    string | undefined,
    ApiClientResponseError<DefaultResponseErrorDataObject>
  >({
    async queryFn({ pageParam: cursor }) {
      const response = await getTransactionItems({
        config: {
          params: {
            cursor,
            limit: pagination.pageSize,
            order: filter.order,
            scope: filter.scope,
            subject_id: filter.subjectId,
            subject_type: filter.subjectType,
            text_content: filter.testimonials,
          },
        },
      });

      return {
        data: response.data.data.map((giving) => createTransactionItemDetail(giving)),
        total: response.data.total,
      };
    },
    initialPageParam: undefined,
    getNextPageParam(lastPage, allPages) {
      if (allPages.length * pagination.pageSize >= lastPage.total) {
        return undefined;
      }

      return lastPage.data.at(-1)?.id;
    },
    queryKey: getTransactionsInfiniteQueryKey({ filter, pagination }),
    ...options,
  });
